import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Row, Col, Card, Table, Modal, Button, Badge, Form, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { LeadsClient /*, CurrentUserClient */ } from '../web-api-client.ts';
import Loader from './Loader.js';
import { convertUtcToLocal } from './utils/utils.js';
import UserContext from '../providers/UserContext.js';
import * as signalR from '@microsoft/signalr';


const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // internal notes
  const [notes, setNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [saveError, setSaveError] = useState('');

  // archive
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState('');
  const [connStatus, setConnStatus] = useState('disconnected');
  const connectionRef = useRef(null);
  const user = useContext(UserContext);

  // ===== Load & normalize =====
  useEffect(() => {
    let cancelled = false;

    (async function load() {
      try {
        setLoading(true);
        setError(null);

        const leadsClient = new LeadsClient();
        const data = await leadsClient.getLeads(user?.dealerId || 0);
        if (cancelled) return;

        setLeads(normalizeLeadsForUI(data));
      } catch (e) {
        console.error(e);
        setError('Failed to load leads.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user?.dealerId]);

  // --- SignalR: connect once we have a dealerId ---
  useEffect(() => {
    // Don’t connect until we actually know the real dealer id
    if (!user?.dealerId || user.dealerId <= 0) return;

    let mounted = true;        // helps with StrictMode double-invoke
    let started = false;       // only stop if we actually started
    //let conn;                  // connection instance inside this effect

    const hubUrl = `/api/hubs/leads?dealerId=${encodeURIComponent(user.dealerId)}`;

    // Build the connection
    const conn = new signalR.HubConnectionBuilder()
      .withUrl(`/api/hubs/leads?dealerId=${encodeURIComponent(user.dealerId)}`, {
        withCredentials: true
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 15000, 30000]) // custom backoff is fine
      .configureLogging(signalR.LogLevel.Information)
      .build();
    // Keep a global handle for console debugging
    window.connection = conn;
    connectionRef.current = conn;
    // Connection lifecycle logs
    conn.onreconnecting(err => {
      if (!mounted) return;
      console.log('[SignalR] reconnecting', err);
      setConnStatus('reconnecting');
    });
    conn.onreconnected(async () => {
      if (!mounted) return;
      console.log('[SignalR] reconnected');
      setConnStatus('connected');
      try { await reloadLeads(); } catch { }
    });
    conn.onclose(async (err) => {
      if (!mounted) return;
      console.log('[SignalR] closed', err);
      setConnStatus('disconnected');

      // If automatic reconnect failed and we’re closed, try a manual restart after a short delay
      setTimeout(async () => {
        if (!mounted) return;
        try {
          await conn.start();
          console.log('[SignalR] manually re-started.');
          setConnStatus('connected');
        } catch (e) {
          console.error('[SignalR] manual re-start failed', e);
          setConnStatus('error');
        }
      }, 3000);
    });

    // Server push handlers
    conn.on('serverHello', (msg) => {
      console.log('[SignalR] serverHello', msg);
    });

    conn.on('leadCreated', (dto) => {
      console.log('[SignalR] leadCreated', dto);
      setLeads(prev => {
        if (!prev) return normalizeLeadsForUI([dto]);
        if (prev.some(x => x.id === dto.id)) return prev;
        return normalizeLeadsForUI([dto, ...prev]);
      });
    });

    conn.on('leadUpdated', (dto) => {
      console.log('[SignalR] leadUpdated', dto);
      patchLeadInStateExternal(dto);
    });

    conn.on('leadArchived', (dto) => {
      console.log('[SignalR] leadArchived', dto);
      setLeads(prev => prev ? prev.filter(l => l.id !== dto.id) : prev);
      setSelected(prev => (prev?.id === dto.id ? { ...prev, isArchived: true } : prev));
    });

    // Start with a timeout so hung handshakes surface an error
    const start = async () => {
      try {
        console.log('[SignalR] starting…', hubUrl);

        const startWithTimeout = Promise.race([
          conn.start(),
          new Promise((_, rej) => setTimeout(() => rej(new Error('SignalR start timeout (8s)')), 8000))
        ]);

        await startWithTimeout;

        if (!mounted) return;
        started = true;
        console.log('[SignalR] started. state=', conn.state);
        setConnStatus('connected');

        // Optional: ask server to say hi so you can see a push immediately
        // (Only works if you added the serverHello in OnConnectedAsync)
        // nothing to call here; server should have already pushed "serverHello"
      } catch (err) {
        if (!mounted) return;
        console.error('[SignalR] start error', err);
        setConnStatus('error');
      }
    };

    start();
    conn.serverTimeoutInMilliseconds = 120_000;  // client waits up to 120s for server pings
    conn.keepAliveIntervalInMilliseconds = 15_000; // client will ping server roughly every 15s

    // Cleanup (don’t stop if we never started; avoids StrictMode race)
    return () => {
      mounted = false;
      if (started && conn) {
        conn.stop().catch(() => { });
      }
    };
  }, [user?.dealerId]); // <-- reconnect if dealerId changes

  // external helper mirrors your patchLeadInState but accepts a dto directly
  const patchLeadInStateExternal = (updated) => {
    setLeads((prev) => {
      if (!prev) return prev;
      const idx = prev.findIndex(l => l.id === updated.id);
      if (idx < 0) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], ...updated };
      next.sort((a, b) => {
        const at = a._createdIso ? Date.parse(a._createdIso) : 0;
        const bt = b._createdIso ? Date.parse(b._createdIso) : 0;
        return bt - at;
      });
      return next;
    });
    setSelected((prev) => (prev?.id === updated.id ? { ...prev, ...updated } : prev));
  };

  const normalizeLeadsForUI = (data) => {
    const vm = (data || []).map((d, idx) => {
      const created = d?.created || d?.timestampUtc || null;
      const vehicleLabel = d?.vehicleLabel || d?.vehicle?.label || null;
      return {
        ...d,
        _id: d?.id ?? idx,
        _createdIso: created,
        _vehicleLabel: vehicleLabel,
      };
    });

    vm.sort((a, b) => {
      const at = a._createdIso ? Date.parse(a._createdIso) : 0;
      const bt = b._createdIso ? Date.parse(b._createdIso) : 0;
      return bt - at;
    });

    return vm;
  };

  // Helper to reload and return list (so caller can use it)
  const reloadLeads = async () => {
    const leadsClient = new LeadsClient();
    const data = await leadsClient.getLeads(user?.dealerId || 0);
    const vm = normalizeLeadsForUI(data);
    setLeads(vm);
    return vm;
  };

  // ===== Open/Close modal (use id lookup for freshest copy) =====
  const openDetailsById = (id) => {
    const latest = leads.find(l => l.id === id);
    if (!latest) return;
    setSelected(latest);
    setNotes(latest.internalNotes || '');
    setSaveError('');
    setArchiveError('');
    setShowModal(true);
  };

  const closeDetails = () => {
    setShowModal(false);
    setSelected(null);
    setNotes('');
    setSaveError('');
    setArchiveError('');
    setShowArchiveConfirm(false);
  };

  // RENDER-TIME FILTER: hide archived
  const minimalRows = useMemo(() => {
    return (leads || [])
      .filter(l => !l.isArchived)
      .map((l) => ({
        key: l._id,
        id: l.id,
        created: l?._createdIso || null,
        vehicle: l?._vehicleLabel || '—',
        name: l?.name || '—',
        phone: l?.phone || null,
        email: l?.email || null,
        helpful: (typeof l?.isHelpful !== 'undefined') ? l.isHelpful : null,
        raw: l, // unused in click (we rely on id lookup)
      }));
  }, [leads]);

  // Update list + selected; if archived, DROP from list
  const patchLeadInState = (updated) => {
    setLeads((prev) => {
      if (!prev) return prev;
      if (updated.isArchived === true) {
        return prev.filter((l) => l.id !== updated.id);
      }
      return prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l));
    });
    setSelected((prev) => (prev ? { ...prev, ...updated } : prev));
  };

  // Build update command
  const buildUpdateCommand = (overrides = {}) => {
    if (!selected) return null;
    const base = {
      id: selected.id,
      dealerId: user?.dealerId || 0,
      inspectionId: selected.inspectionId ?? null,
      vehicleId: selected.vehicleId ?? null,
      name: selected.name ?? null,
      email: selected.email ?? null,
      phone: selected.phone ?? null,
      message: selected.message ?? null,
      isHelpful: selected.isHelpful ?? null,
      contactMe: selected.contactMe ?? null,
      internalNotes: selected.internalNotes ?? null,
      isArchived: selected.isArchived ?? false,
    };
    return { ...base, ...overrides };
  };

  // ===== Save notes =====
  const saveNotes = async () => {
    if (!selected) return;
    setIsSavingNotes(true);
    setSaveError('');

    try {
      const leadsClient = new LeadsClient();
      const command = buildUpdateCommand({ internalNotes: notes });

      await leadsClient.updateLead(selected.id, command);

      // Optimistic update
      patchLeadInState({ id: selected.id, internalNotes: notes });

      // Server-truth refresh; reselect fresh record if modal is still open
      const fresh = await reloadLeads();
      if (showModal) {
        const latest = fresh.find(x => x.id === selected.id);
        if (latest) {
          setSelected(latest);
          setNotes(latest.internalNotes || '');
        }
      }
    } catch (err) {
      console.error(err);
      setSaveError('Failed to save notes. Please try again.');
    } finally {
      setIsSavingNotes(false);
    }
  };

  // ===== Archive =====
  const archiveLead = async () => {
    if (!selected) return;
    setIsArchiving(true);
    setArchiveError('');

    try {
      const leadsClient = new LeadsClient();
      const command = buildUpdateCommand({ isArchived: true });

      await leadsClient.updateLead(selected.id, command);

      // Optimistic update (remove from list)
      patchLeadInState({ id: selected.id, isArchived: true });

      // Refresh from server to keep state 100% in sync
      await reloadLeads();

      // Close modals
      setShowArchiveConfirm(false);
      setShowModal(false);
      setSelected(null);
    } catch (err) {
      console.error(err);
      setArchiveError('Failed to archive lead. Please try again.');
    } finally {
      setIsArchiving(false);
    }
  };

  return (
    <Container fluid className="p-4">
      {/* Top navigation area */}
      <Row className="mb-3">
        <Col>
          <h4 className="fw-bold">My Dashboard</h4>
        </Col>
      </Row>

      {/* Quick Actions */}
      <Row className="mb-4">
        <Col md={6}>
          <Link className="nav-link" to="/dvi/new">
            <Card className="p-3 mb-3" style={{ background: 'rgb(94 128 82)' }}>
              <Card.Body>
                <h1 className="fw-bold">New Inspection</h1>
                <Card.Text>Create a new Vehicle Inspection</Card.Text>
              </Card.Body>
            </Card>
          </Link>
        </Col>
        <Col md={6}>
          <Link className="nav-link" to="/print-qrs">
            <Card className="p-3 mb-3" style={{ background: 'rgb(94 128 82)' }}>
              <Card.Body>
                <h1 className="fw-bold">Print QRs</h1>
                <Card.Text>Print QR codes for vehicle inspections</Card.Text>
              </Card.Body>
            </Card>
          </Link>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={6}>
          <Link className="nav-link" to="/dvis?status=0&sort=created&dir=desc&page=1">
            <Card className="p-3 mb-3" style={{ background: 'rgb(94 128 82)' }}>
              <Card.Body>
                <h1 className="fw-bold">In Progress</h1>
                <Card.Text>View In-Progress Inspections</Card.Text>
              </Card.Body>
            </Card>
          </Link>
        </Col>
        <Col md={6}>
          <Link className="nav-link" to="/dvis?sort=created&dir=desc&page=1">
            <Card className="p-3 mb-3" style={{ background: 'rgb(94 128 82)' }}>
              <Card.Body>
                <h1 className="fw-bold">History</h1>
                <Card.Text>View All Inspection History</Card.Text>
              </Card.Body>
            </Card>
          </Link>
        </Col>
      </Row>

      {/* Recent Leads */}
      <Row className="mt-2">
        <Col md={12}>
          <Card className="mb-4">
            <Card.Header className="d-flex align-items-center justify-content-between">
              <span className="fw-semibold">Recent Leads</span>
              {connStatus !== 'connected' && (
                <small className="ms-2 text-muted">({connStatus})</small>
              )}

              {loading && <small className="text-muted">Loading…</small>}
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="p-4"><Loader /></div>
              ) : error ? (
                <div className="p-4 text-danger">{error}</div>
              ) : (
                <div className="table-responsive">
                  <Table hover striped className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th style={{ whiteSpace: 'nowrap' }}>Date</th>
                        <th>Interested Vehicle</th>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Email</th>
                        <th className="text-end" style={{ width: 48 }} aria-hidden />
                      </tr>
                    </thead>
                    <tbody>
                      {minimalRows.map((row) => (
                        <tr
                          key={row.key}
                          role="button"
                          onClick={() => openDetailsById(row.id)}
                          title="Click for details"
                          className="clickable-row"
                        >
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {row.created ? new Date(row.created).toLocaleString(undefined, {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                  hour12: true
                }) : '—'}
                          </td>
                          <td>{row.vehicle}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {row.name}
                            {/* {row.helpful !== null && (
                              <Badge
                                bg={(String(row.helpful).toLowerCase() === 'true' || String(row.helpful).toLowerCase() === 'yes') ? 'success' : 'secondary'}
                                className="ms-2"
                              >
                                {(String(row.helpful).toLowerCase() === 'true' || String(row.helpful).toLowerCase() === 'yes') ? 'Helpful' : '—'}
                              </Badge>
                            )} */}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {row.phone ? (
                              row.phone
                            ) : '—'}
                          </td>
                          <td className="text-truncate" style={{ maxWidth: 220 }}>
                            {row.email ? (
                              row.email
                            ) : '—'}
                          </td>
                          <td className="text-end text-muted">
                            <i className="bi bi-chevron-right" />
                          </td>
                        </tr>
                      ))}
                      {minimalRows.length === 0 && (
                        <tr>
                          <td colSpan={6} className="text-center text-muted py-4">
                            No recent leads
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Lead Details Modal */}
      <Modal show={showModal} onHide={closeDetails} centered size="lg" backdrop="static">
        <Modal.Header closeButton className="border-0 pb-0">
          <div className="w-100">
            <div className="d-flex align-items-center justify-content-between">
              <Modal.Title className="d-flex align-items-center gap-2">
                <span className="rounded-circle bg-primary bg-opacity-10 text-primary d-inline-flex align-items-center justify-content-center"
                  style={{ width: 36, height: 36 }}>
                  <i className="bi bi-person-lines-fill" />
                </span>
                Lead Details
              </Modal.Title>

              {/* Submitted timestamp */}
              <div className="text-end">
                <div className="text-muted small">Submitted</div>
                <div className="fw-semibold">{selected ? convertUtcToLocal(selected.created) : '—'}</div>
              </div>
            </div>

            {/* Vehicle chip under title */}
            {selected && (
              <div className="mt-2 h5">
                <span className="badge text-bg-light border fw-normal">
                  <i className="bi bi-car-front me-1" />
                  {selected._vehicleLabel || 'Vehicle: —'}
                </span>
              </div>
            )}
          </div>
        </Modal.Header>

        <Modal.Body className="pt-3">
          {!selected ? (
            <Loader />
          ) : (
            <div className="container-fluid px-0">
              <div className="row g-4">
                {/* Left: Person card */}
                <div className="col-md-5">
                  <div className="card shadow-sm border-0">
                    <div className="card-body">
                      <div className="d-flex align-items-start gap-3">
                        <div className="flex-grow-1">
                          <div className="fw-semibold fs-5 mb-1">{selected.name || '—'}</div>

                          {/* Email */}
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <i className="bi bi-envelope text-muted" />
                            {selected.email ? (
                              <a href={`mailto:${selected.email}`} className="link-primary text-truncate">{selected.email}</a>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </div>

                          {/* Phone */}
                          <div className="d-flex align-items-center gap-2 mb-3">
                            <i className="bi bi-telephone text-muted" />
                            {selected.phone ? (
                              <a href={`tel:${selected.phone}`} className="link-primary">{selected.phone}</a>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </div>

                          {/* Quick actions */}
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              disabled={!selected.email}
                              as={selected.email ? 'a' : 'button'}
                              href={selected.email ? `mailto:${selected.email}` : undefined}
                            >
                              <i className="bi bi-send me-1" />
                              Email
                            </Button>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              disabled={!selected.phone}
                              as={selected.phone ? 'a' : 'button'}
                              href={selected.phone ? `tel:${selected.phone}` : undefined}
                            >
                              <i className="bi bi-telephone-outbound me-1" />
                              Call
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Comments */}
                      <hr className="text-muted opacity-25" />
                      <div>
                        <div className="text-muted small mb-1">Comments</div>
                        <div className="text-body" style={{ whiteSpace: 'pre-wrap' }}>
                          {selected.message || <span className="text-muted">—</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: Meta details + Internal Notes */}
                <div className="col-md-7">
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-body">
                      <div className="text-muted small mb-2">Lead Preferences</div>
                      <div className="d-flex flex-wrap gap-2 mb-3">
                        <span className="d-inline-flex align-items-center gap-2">
                          <span className="text-muted small">Helpful</span>
                          {selected.isHelpful === null || typeof selected.isHelpful === 'undefined' ? (
                            <span className="text-muted">—</span>
                          ) : selected.isHelpful ? (
                            <span className="badge text-bg-success-subtle border border-success-subtle">Yes</span>
                          ) : (
                            <span className="badge text-bg-secondary-subtle border">No</span>
                          )}
                        </span>
                        <span className="d-inline-flex align-items-center gap-2">
                          <span className="text-muted small">Send More Info</span>
                          {selected.contactMe === null || typeof selected.contactMe === 'undefined' ? (
                            <span className="text-muted">—</span>
                          ) : selected.contactMe ? (
                            <span className="badge text-bg-success-subtle border border-success-subtle">Yes</span>
                          ) : (
                            <span className="badge text-bg-secondary-subtle border">No</span>
                          )}
                        </span>
                      </div>

                      <div className="text-muted small mb-2">Links</div>
                      <div className="d-flex flex-wrap gap-2 mb-4">
                        <Link
                          to={`/dvi/${selected.inspectionId}`}
                          className={`btn btn-outline-secondary btn-sm ${!selected.inspectionId ? 'disabled' : ''}`}
                        >
                          <i className="bi bi-clipboard2-pulse me-1" />
                          View Inspection
                        </Link>
                        <Link
                          to={`/vehicles/${selected.vehicleId}`}
                          className={`btn btn-outline-secondary btn-sm ${!selected.vehicleId ? 'disabled' : ''}`}
                        >
                          <i className="bi bi-car-front me-1" />
                          View Vehicle
                        </Link>
                      </div>

                      {/* Internal Notes */}
                      <div className="mb-2 d-flex align-items-center justify-content-between">
                        <div className="text-muted small">Internal Notes</div>
                        {selected.isArchived && (
                          <span className="badge bg-secondary">Archived</span>
                        )}
                      </div>
                      <Form.Group className="mb-2">
                        <Form.Control
                          as="textarea"
                          rows={4}
                          placeholder="Add private notes for your team…"
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          disabled={isSavingNotes || isArchiving}
                        />
                      </Form.Group>
                      {saveError && <div className="text-danger small mb-2">{saveError}</div>}
                      <div className="d-flex gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={saveNotes}
                          disabled={isSavingNotes || isArchiving}
                        >
                          {isSavingNotes ? <Spinner size="sm" animation="border" className="me-2" /> : <i className="bi bi-save me-1" />}
                          Save Notes
                        </Button>

                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => setShowArchiveConfirm(true)}
                          disabled={isArchiving || selected.isArchived}
                        >
                          <i className="bi bi-archive me-1" />
                          Archive
                        </Button>
                        {archiveError && <div className="text-danger small ms-2 align-self-center">{archiveError}</div>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0">
          <Button variant="secondary" onClick={closeDetails} disabled={isSavingNotes || isArchiving}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Archive Confirmation Modal */}
      <Modal show={showArchiveConfirm} onHide={() => setShowArchiveConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Archive Lead</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selected?.isArchived ? (
            <div className="text-muted">This lead is already archived.</div>
          ) : (
            <>
              <p className="mb-2">
                Are you sure you want to archive this lead?
              </p>
            </>
          )}
          {archiveError && <div className="text-danger small mt-3">{archiveError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowArchiveConfirm(false)} disabled={isArchiving}>
            Cancel
          </Button>
          <Button variant="danger" onClick={archiveLead} disabled={isArchiving || selected?.isArchived}>
            {isArchiving ? <Spinner size="sm" animation="border" className="me-2" /> : <i className="bi bi-archive me-1" />}
            Confirm Archive
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Dashboard;
